import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 18px 60px rgba(16, 24, 40, 0.18)"
  },
  title: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2)
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2)
  },
  notice: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.warning.light}`,
    background: theme.palette.warning.light + "1f"
  },
  details: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    gap: theme.spacing(1)
  }
}));

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const visible = open === "create" || open === "edit";
  const currentSettings = data?.data?.typebotIntegration || {};
  const legacy = open === "edit" && !currentSettings.promptId;
  const [promptId, setPromptId] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setPromptId(currentSettings.promptId || "");
    setSearch("");
  }, [currentSettings.promptId, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const requests = [api.get("/prompt", {
          params: { searchParam: search, pageNumber: 1, pageSize: 50 }
        })];
        if (currentSettings.promptId) {
          requests.push(api.get(`/prompt/${currentSettings.promptId}`));
        }
        const [listResponse, selectedResponse] = await Promise.all(requests);
        if (active) {
          const loaded = listResponse.data.prompts || [];
          const selected = selectedResponse?.data;
          setPrompts(selected && !loaded.some(item => item.id === selected.id)
            ? [selected, ...loaded]
            : loaded);
        }
      } catch (error) {
        if (active) toastError(error);
      } finally {
        if (active) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [currentSettings.promptId, search, visible]);

  const selectedPrompt = useMemo(
    () => prompts.find(prompt => Number(prompt.id) === Number(promptId)),
    [promptId, prompts]
  );

  const handleClose = () => close(null);

  const handleSave = () => {
    if (!promptId && !legacy) return;
    setSaving(true);
    const typebotIntegration = promptId
      ? { promptId: Number(promptId) }
      : currentSettings;
    if (open === "edit") {
      onUpdate({
        ...data,
        data: { ...data.data, typebotIntegration }
      });
    } else {
      onSave({ typebotIntegration });
    }
    setSaving(false);
    handleClose();
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Box>
          <Typography variant="h6">
            {open === "edit" ? "Editar agente do fluxo" : "Adicionar agente ao fluxo"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            O nó usa as mesmas instruções, modelo, bases e política de encaminhamento do agente.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {legacy && (
          <Box className={classes.notice}>
            <Typography variant="subtitle2">Configuração legada</Typography>
            <Typography variant="body2">
              Este nó continua funcionando com a configuração atual. Selecione um agente para convertê-lo explicitamente ao formato unificado.
            </Typography>
          </Box>
        )}

        <TextField
          value={search}
          onChange={event => setSearch(event.target.value)}
          label="Buscar agente"
          variant="outlined"
          size="small"
          fullWidth
        />

        <FormControl variant="outlined" fullWidth required={!legacy}>
          <InputLabel id="flow-agent-label">Agente de IA</InputLabel>
          <Select
            labelId="flow-agent-label"
            value={promptId}
            onChange={event => setPromptId(event.target.value)}
            label="Agente de IA"
            disabled={loading}
          >
            {legacy && <MenuItem value="">Manter configuração legada</MenuItem>}
            {prompts.map(prompt => (
              <MenuItem key={prompt.id} value={prompt.id}>
                {prompt.name}
              </MenuItem>
            ))}
          </Select>
          {loading && (
            <Box mt={1} display="flex" alignItems="center" gridGap={8}>
              <CircularProgress size={16} />
              <Typography variant="caption">Carregando agentes…</Typography>
            </Box>
          )}
        </FormControl>

        {selectedPrompt && (
          <Box className={classes.details}>
            <Typography variant="subtitle2">{selectedPrompt.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedPrompt.prompt}
            </Typography>
            <Box className={classes.chips}>
              <Chip size="small" variant="outlined" label={selectedPrompt.model} />
              <Chip
                size="small"
                variant="outlined"
                label={selectedPrompt.knowledge?.mode === "disabled"
                  ? "Sem RAG"
                  : `${selectedPrompt.knowledge?.bases?.length || 0} base(s) · RAG ${selectedPrompt.knowledge?.mode}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${selectedPrompt.bindings?.total || 0} vínculo(s)`}
              />
            </Box>
          </Box>
        )}

        {!loading && prompts.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Nenhum agente encontrado. Crie e teste um agente na área Agentes de IA antes de vinculá-lo ao fluxo.
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || (!promptId && !legacy)}
        >
          {legacy && !promptId ? "Manter legado" : "Salvar agente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderOpenAIModal;
